import { message } from 'antd';
import { httpService } from '../utils/http';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ValidateTokenResponse,
  UserInfo,
  ApiMenuItem,
} from '../types';

/**
 * 认证服务类
 * 处理用户登录、登出、令牌刷新、令牌验证等功能
 */
export class AuthService {



  /**
   * 用户登录
   * @param credentials 登录凭据
   * @returns Promise<LoginResponse>
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await httpService.postWithoutAuth('/auth/login', credentials);
      
      const { data } = response;
      
      // 保存令牌和用户信息
      this.saveTokens(data.accessToken, data.refreshToken);
      this.saveUserInfo(data.userInfo);
      this.saveMenus(data.menus);
      this.savePermissions(data.permissions);
      
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 用户登出
   * @returns Promise<void>
   */
  async logout(): Promise<void> {
    try {
      await httpService.post('/auth/logout');
      this.clearAuthData();
    } catch (error) {
      // 即使登出失败，也要清除本地数据
      this.clearAuthData();
      throw error;
    }
  }

  /**
   * 刷新访问令牌
   * @param refreshToken 刷新令牌
   * @returns Promise<RefreshTokenResponse>
   */
  async refreshToken(refreshToken?: string): Promise<RefreshTokenResponse> {
    try {
      const token = refreshToken || this.getRefreshToken();
      if (!token) {
        throw new Error('刷新令牌不存在');
      }

      const response = await httpService.postWithoutAuth('/auth/refresh', { 
        refreshToken: token 
      } as RefreshTokenRequest);
      
      const { data } = response;
      
      // 更新令牌
      this.saveTokens(data.accessToken, data.refreshToken);
      
      return data;
    } catch (error) {
      // 刷新失败，清除认证数据
      this.clearAuthData();
      throw error;
    }
  }

  /**
   * 验证令牌有效性
   * @param token 访问令牌
   * @returns Promise<boolean>
   */
  async validateToken(token?: string): Promise<boolean> {
    try {
      const accessToken = token || this.getAccessToken();
      if (!accessToken) {
        return false;
      }

      const response = await httpService.get('/auth/validate');
      
      // 检查响应状态码，1表示成功
      if (response.status === 1) {
        return response.data === true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * 获取当前用户信息
   * @returns Promise<string> 用户名
   */
  async getCurrentUser(): Promise<string> {
    try {
      const response = await httpService.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 检查用户是否已认证
   * @returns boolean
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token;
  }

  /**
   * 获取访问令牌
   * @returns string | null
   */
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * 获取刷新令牌
   * @returns string | null
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * 获取用户信息
   * @returns UserInfo | null
   */
  getUserInfo(): UserInfo | null {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * 获取用户菜单
   * @returns ApiMenuItem[]
   */
  getMenus(): ApiMenuItem[] {
    const menus = localStorage.getItem('userMenus');
    return menus ? JSON.parse(menus) : [];
  }

  /**
   * 获取用户权限
   * @returns string[]
   */
  getPermissions(): string[] {
    const permissions = localStorage.getItem('userPermissions');
    return permissions ? JSON.parse(permissions) : [];
  }

  /**
   * 检查用户是否有指定权限
   * @param permission 权限标识
   * @returns boolean
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    return permissions.includes(permission);
  }

  /**
   * 检查用户是否有指定权限中的任意一个
   * @param permissions 权限标识数组
   * @returns boolean
   */
  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.getPermissions();
    return permissions.some(permission => userPermissions.includes(permission));
  }

  /**
   * 检查用户是否有指定权限中的所有权限
   * @param permissions 权限标识数组
   * @returns boolean
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.getPermissions();
    return permissions.every(permission => userPermissions.includes(permission));
  }

  /**
   * 保存令牌
   * @param accessToken 访问令牌
   * @param refreshToken 刷新令牌
   */
  private saveTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  /**
   * 保存用户信息
   * @param userInfo 用户信息
   */
  private saveUserInfo(userInfo: UserInfo): void {
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  }

  /**
   * 保存用户菜单
   * @param menus 菜单列表
   */
  private saveMenus(menus: ApiMenuItem[]): void {
    localStorage.setItem('userMenus', JSON.stringify(menus));
  }

  /**
   * 保存用户权限
   * @param permissions 权限列表
   */
  private savePermissions(permissions: string[]): void {
    localStorage.setItem('userPermissions', JSON.stringify(permissions));
  }

  /**
   * 清除认证数据
   */
  private clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userMenus');
    localStorage.removeItem('userPermissions');
  }


}

// 创建默认实例
export const authService = new AuthService();

// 导出默认实例
export default authService; 